import _ from 'lodash';
import type {
  DirectiveNode,
  OperationDefinitionNode,
  FragmentDefinitionNode,
  SelectionNode,
} from 'graphql';
import {argumentsObjectFromField} from '@apollo/client/utilities';
import {
  bucketNodesByPath,
  truncateIncludeOnly,
  ROOT_PATH,
} from '@stripe-internal/babel-plugin-data-graphql-helpers';
import type {NodeLookupMap} from '@stripe-internal/babel-plugin-data-graphql-helpers';
import {rejectTypenameDeep} from './rejectTypenameDeep';
import stringify from './stringify';

import type {QsGeneric} from './stringify';
import type {
  RestLinkOperationContext,
  QueryInput,
  BodyBuilderParams,
  RestDirective,
  NodeLookupMapArgument,
  PathParamAccumulator,
  PredicateNode,
  ParamsDirective,
  FragmentDefinitionLookupMap,
  PathParamsFragment,
} from './types';

const leadingSlashRegex = /^\//;
const pathVariableRegex = /:([^/]*)/g;
const snakeCaseSkipList = new Set(['line1', 'line2', 'last4']);
const ignoredVariables: {[key: string]: true} = {__typename: true};

const PAYMENTS_LIST_AVOID_FIELDS = new Set(['data.unified_instrument']);

// Fields which are expensive to expand should be added here to override
// the default behavior of truncating fields with the most nodes.
const FIELDS_TO_AVOID_BY_OPERATION_NAME: {
  [operationName: string]: Set<string>;
} = {
  BapiPaymentsTableListQuery: PAYMENTS_LIST_AVOID_FIELDS,
  BentoPaymentsTableListQuery: PAYMENTS_LIST_AVOID_FIELDS,
};

const getUrlTokenMatches = (path: string) => [
  ...path.matchAll(pathVariableRegex),
];

const parsePathWithVariables = (
  path: string,
  input: QueryInput,
): {
  parsedPath: string;
  pathVariableKeys: ReadonlyArray<string>;
} => {
  return getUrlTokenMatches(path).reduce(
    (acc, pathVariableMatch) => {
      const pathMatch = pathVariableMatch[0];
      const pathVariable = pathVariableMatch[1];
      const matchingArg = input[pathVariable];

      return {
        parsedPath: acc.parsedPath.replace(
          pathMatch,
          encodeURIComponent(matchingArg),
        ),
        pathVariableKeys: acc.pathVariableKeys.concat(pathVariable),
      };
    },

    {
      parsedPath: path,
      pathVariableKeys: [] as string[],
    },
  );
};

export const bodyBuilder = (
  params: BodyBuilderParams,
): {
  [key: string]: string;
} => {
  const {
    args,
    '@rest': {runtimePath: path},
  } = params;

  const keysToOmit: {[key: string]: true} = {};

  Object.keys(ignoredVariables).forEach((key) => {
    keysToOmit[key] = true;
  });

  getUrlTokenMatches(path).forEach(([, key]) => {
    keysToOmit[key] = true;
  });

  if (args?.input) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Object.keys(args.input).reduce<Record<string, any>>((acc, key) => {
      if (args.input && !keysToOmit[key]) {
        return {
          ...acc,
          [key]: rejectTypenameDeep(args.input[key]),
        };
      } else {
        return acc;
      }
    }, {});
  } else {
    return {};
  }
};

const buildRequestURL = (
  restDirective: RestDirective,
  input: QueryInput,
  predicateVariables: Set<string>,
  query: {
    expand: ReadonlyArray<string>;
    include_only: ReadonlyArray<string>;
  },
  observabilityFns: RestLinkOperationContext['observabilityFns'],
): string => {
  const {runtimePath, method} = restDirective;

  const {parsedPath, pathVariableKeys} = parsePathWithVariables(
    runtimePath.replace(leadingSlashRegex, ''),
    input,
  );

  const requestQueryParams: {[key: string]: QsGeneric} = {...query};

  // Appending additional input args as GET / DELETE query params.
  if (method === 'GET' || method === 'DELETE') {
    Object.keys(input).forEach((key) => {
      if (
        !pathVariableKeys.includes(key) &&
        !predicateVariables.has(key) &&
        !ignoredVariables[key]
      ) {
        let newKey = key;

        if (!snakeCaseSkipList.has(key)) {
          if (/\d/.test(key)) {
            observabilityFns?.errors?.warning(
              'request query param has number but is being snake-cased',
              {
                project: 'sail_core',
                extras: {key},
              },
            );
          }

          newKey = _.snakeCase(key);
        }

        requestQueryParams[newKey] = input[key];
      }
    });
  }

  const queryString = stringify(requestQueryParams);

  return `${parsedPath}${queryString ? '?' : ''}${queryString}`;
};

const mapLookupMapToArgument = (
  nodeLookupMap: NodeLookupMap,
): NodeLookupMapArgument => {
  const args: Array<{
    nodes: Array<string>;
    parentPath: string;
  }> = [];

  Object.keys(nodeLookupMap).forEach((path) => {
    args.push({
      parentPath: path,
      nodes: nodeLookupMap[path],
    });
  });

  return args;
};

const evaluatePredicates = (
  accumulator: PathParamAccumulator,
  node: PredicateNode,
  input: QueryInput,
  parentExpandParam = '',
) => {
  // Evaluate the current node.
  let appendCurrentNodeInfo = false;

  if (node.type === 'include') {
    appendCurrentNodeInfo = input[node.variable];
  } else if (node.type === 'skip') {
    appendCurrentNodeInfo = !input[node.variable];
  }

  if (appendCurrentNodeInfo) {
    accumulator.includeOnly = accumulator.includeOnly.concat(node.includeOnly);
    accumulator.fragments = accumulator.fragments.concat(node.fragments);

    accumulator.nodeLookupMap = accumulator.nodeLookupMap.concat(
      mapLookupMapToArgument(bucketNodesByPath(node.includeOnly)),
    );

    // Only expand children if the current predicate node is true.
    if (node.children.length) {
      // End of the expand param, but there are more predicate nodes to be
      // evaluated.
      if (!node.expand && parentExpandParam) {
        accumulator.expands.push(parentExpandParam);
      }

      node.children.forEach((childNode) => {
        evaluatePredicates(accumulator, childNode, input, node.expand);
      });
    } else {
      const expand = node.expand || parentExpandParam;

      if (expand) {
        accumulator.expands.push(expand);
      }
    }
  } else if (parentExpandParam) {
    // If the current predicate node is evaluated to false but it's parents has
    // an expand param.
    accumulator.expands.push(parentExpandParam);
  }
};

const extractParamsDirective = (
  operationName: string,
  directives: ReadonlyArray<DirectiveNode> | null | undefined,
  input: QueryInput,
  isDashboardOnlyAPIKey: boolean,
): ParamsDirective => {
  const pathParams: ParamsDirective = {
    expands: [],
    includeOnly: [],
    fragments: [],
    nodeLookupMap: [],
    predicateVariables: [],
  };

  const paramsDirective = directives?.find(({name}) => name.value === 'params');

  if (paramsDirective) {
    const argumentsObject: any = argumentsObjectFromField(paramsDirective);

    // Base params.
    pathParams.expands = argumentsObject.expand;
    pathParams.fragments = argumentsObject.fragments;
    pathParams.nodeLookupMap = argumentsObject.nodeLookupMap;

    // The `include_only` request param is only supported for dashboard-only API keys.
    if (isDashboardOnlyAPIKey) {
      pathParams.includeOnly = argumentsObject.includeOnly;
    }

    // Conditional params.
    if (!_.isEmpty(argumentsObject.predicates)) {
      argumentsObject.predicates.ROOT.children.forEach(
        (topLevelNode: PredicateNode) => {
          evaluatePredicates(pathParams, topLevelNode, input);
        },
      );

      pathParams.predicateVariables = argumentsObject.predicates.ROOT.variables;
    }
  } else {
    throw new Error(
      `[client-proxy]: @params is not defined in: ${operationName}`,
    );
  }

  return pathParams;
};

const buildFragmentDefinitionsLookup = (
  fragmentDefinitions: ReadonlyArray<FragmentDefinitionNode>,
): FragmentDefinitionLookupMap => {
  const result: {[key: string]: FragmentDefinitionNode} = {};

  fragmentDefinitions.forEach((fragmentDefinition) => {
    result[fragmentDefinition.name.value] = fragmentDefinition;
  });

  return result;
};

/**
 * At a high level, this function return all permutations of possible paths
 * that leads to the current fragment. For instance, given a fragmentPaths:
 *
 * [
 *   // We have an array of array here because there are two possible paths to
 *   // the first fragment, ie. the first fragment is being used at both
 *   // `customer.data` and `account.data` within the query
 *   [customer.data, account.data],
 *   [creditCard],
 *   [balance.amount],
 * ]
 *
 * The result would be:
 *
 * [
 *   customer.data.creditCard.balance.amount,
 *   account.data.creditCard.balance.amount,
 * ]
 *
 * Algorithm origin: https://stackoverflow.com/questions/5751091/permutations-of-an-array-of-arrays-of-strings
 */
const getAllPathPermutations = (
  fragmentPaths: Array<PathParamsFragment>,
): Array<string> => {
  const permutations: Array<string> = [];
  const paths = fragmentPaths.map(({paths}) => paths);

  let iterator = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const pick: Array<string> = [];
    let num = iterator++;

    paths.forEach((path) => {
      const r = num % path.length;
      num = (num - r) / path.length;
      pick.push(path[r]);
    });

    if (num > 0) {
      break;
    }

    permutations.push(pick.filter(Boolean).join('.'));
  }

  return permutations;
};

const getFullPath = (parentPath: string, param: string) => {
  if (parentPath) {
    return `${parentPath}.${param}`;
  }

  return param;
};

class ParamsAccumulator {
  includeOnly: Set<string>;

  expands: Set<string>;

  predicateVariables: Set<string>;

  nodeLookupMap: NodeLookupMap;

  constructor(params: ParamsDirective) {
    this.includeOnly = new Set(params.includeOnly);
    this.expands = new Set(params.expands);
    this.predicateVariables = new Set(params.predicateVariables);
    this.nodeLookupMap = {};
    this._appendToNodeLookupMap(ROOT_PATH, params.nodeLookupMap);
  }

  _appendToNodeLookupMap(
    parentPath: string,
    nodeLookupMapArgument: NodeLookupMapArgument,
  ) {
    nodeLookupMapArgument.forEach(({parentPath: path, nodes}) => {
      let fullPath;

      if (parentPath === ROOT_PATH) {
        // Root level lookup map.
        fullPath = path;
      } else {
        // Each fragment.
        fullPath = path === ROOT_PATH ? parentPath : `${parentPath}.${path}`;
      }

      const existingNodes = this.nodeLookupMap[fullPath] || [];

      this.nodeLookupMap[fullPath] = Array.from(
        new Set([...existingNodes, ...nodes]),
      );
    });
  }

  append(namespace: string, params: ParamsDirective) {
    params.predicateVariables.forEach((variable) => {
      this.predicateVariables.add(variable);
    });

    params.includeOnly.forEach((include) => {
      this.includeOnly.add(getFullPath(namespace, include));
    });

    params.expands.forEach((expand) => {
      const fullPath = getFullPath(namespace, expand);
      const toRemove: Array<string> = [];

      this.expands.forEach((eachExpand) => {
        // We are doing BFS so we are looking for sub-expands to remove.
        if (fullPath.startsWith(`${eachExpand}.`)) {
          toRemove.push(eachExpand);
        }
      });

      this.expands.add(fullPath);
      toRemove.forEach(this.expands.delete, this.expands);
    });

    this._appendToNodeLookupMap(namespace || ROOT_PATH, params.nodeLookupMap);
  }
}

const mergeFragmentParams = (
  input: QueryInput,
  baseOperationName: string,
  params: ParamsDirective,
  currentPath: Array<PathParamsFragment>,
  fragmentDefinitionLookup: FragmentDefinitionLookupMap,
  accumulator: ParamsAccumulator,
  isDashboardOnlyAPIKey: boolean,
) => {
  getAllPathPermutations(currentPath).forEach((pathToFragment) => {
    accumulator.append(pathToFragment, params);
  });

  if (!_.isEmpty(params.fragments)) {
    params.fragments.forEach((fragment) => {
      const fragmentDefinition = fragmentDefinitionLookup[fragment.name];

      const fragmentParams = extractParamsDirective(
        `${baseOperationName} > ${fragment.name}`,
        fragmentDefinition.directives,
        input,
        isDashboardOnlyAPIKey,
      );

      mergeFragmentParams(
        input,
        baseOperationName,
        fragmentParams,
        [...currentPath, fragment],
        fragmentDefinitionLookup,
        accumulator,
        isDashboardOnlyAPIKey,
      );
    });
  }
};

const getOperationSelection = (
  selections: ReadonlyArray<SelectionNode>,
  queryName: string,
) =>
  selections.find((selection) => {
    if (selection.kind === 'Field') {
      return (
        queryName === selection.alias?.value ||
        queryName === selection.name.value
      );
    }

    return false;
  });

export const runtimePathBuilder = (
  operationName: string,
  restDirective: RestDirective,
  operationDefinition: OperationDefinitionNode,
  fragmentDefinitions: ReadonlyArray<FragmentDefinitionNode>,
  input: QueryInput,
  isDashboardOnlyAPIKey: boolean,
  observabilityFns: RestLinkOperationContext['observabilityFns'],
  convertGetQueryToPostBody?: boolean,
): string => {
  const operationSelection = getOperationSelection(
    operationDefinition.selectionSet.selections,
    restDirective.queryName,
  );

  const operationParams = extractParamsDirective(
    operationName,
    // Only the top level directives of the operation since we already saved
    // all the path param info from the operation with @params during the build
    // time.
    operationSelection?.directives,
    input,
    isDashboardOnlyAPIKey,
  );

  const fragmentDefinitionsLookup =
    buildFragmentDefinitionsLookup(fragmentDefinitions);

  const accumulator = new ParamsAccumulator(operationParams);

  mergeFragmentParams(
    input,
    operationName,
    operationParams,
    [],
    fragmentDefinitionsLookup,
    accumulator,
    isDashboardOnlyAPIKey,
  );

  const originalIncludeOnly = Array.from(accumulator.includeOnly);
  const includeOnly = convertGetQueryToPostBody
    ? originalIncludeOnly // We use POST to avoid truncating, letting us add more includeOnly contents
    : truncateIncludeOnly(
        originalIncludeOnly,
        accumulator.nodeLookupMap,
        FIELDS_TO_AVOID_BY_OPERATION_NAME[operationName] ?? new Set(),
      );

  const truncatedFields = includeOnly.filter(
    (param) => !accumulator.includeOnly.has(param),
  );
  if (truncatedFields.length) {
    observabilityFns?.analytics?.track('rest_link_truncation', {
      project: 'sail_core',
      operation_name: operationName,
      operation_truncated_fields: truncatedFields.join(','),
    });
  }

  return buildRequestURL(
    restDirective,
    input,
    accumulator.predicateVariables,
    {
      expand: Array.from(accumulator.expands),
      include_only: [includeOnly.join(',')].filter(Boolean),
    },
    observabilityFns,
  );
};
